<script>
export default {
  name: 'DatePicker'
}

// カレンダー要素は全インスタンスで共有されるため、表示中の対象を記録する
let currentOpenIdentifier = null;

// 識別子ごとのクリア処理（クリアボタンも全インスタンス共有）
const clearHandlers = new Map();
</script>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { Capacitor } from '@capacitor/core';

const props = defineProps({
  modelValue: {
    type: String,
    required: true
  },
  identifier: {
    type: String,
    required: true
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false
  },
  readonly: {
    type: Boolean,
    required: false,
    default: () => Capacitor.isNativePlatform()
  }
});

const inputValue = ref('');

const emit = defineEmits( ['update:modelValue'] )

// 正規表現
const regexp = /^([0-9]{4})\/(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/;
const regexp_relief = /^([0-9]{4})\/([0-9]{1,2})\/([0-9]{1,2})$/;

// カレンダーが表示されているか
const isDatepickerOpen = ref(false);

// エンターキーが押されたかどうかのフラグ
const isEnterPressed = ref(false);

// 日付入力をフォーマットしてモデルと入力欄を更新する
const updateDateValue = (inputText) => {
  const formattedText = regexp_relief.test(inputText)
    ? inputText.replace(regexp_relief, paddingReplacer)
    : inputText;

  emit('update:modelValue', formattedText);
  updateInputElementValue(formattedText);
}

// 入力欄を更新する
const updateInputElementValue = (value) => {
  const inputElement = document.getElementById(props.identifier);
  if (inputElement) {
    inputElement.value = value;
  }
}

// フォーカスが外れた時の処理
const onBlur = (e) => {
  if (!isDatepickerOpen.value) {
    updateDateValue(e.target.value);
  }
}

// Enterキーを押下した時の処理
const onEnter = (e) => {
  // エンターキーが押されたことをマーク
  isEnterPressed.value = true;

  // 少し遅らせてフラグをリセット（onSelectの処理後）
  setTimeout(() => {
    isEnterPressed.value = false;
  }, 10);
}

// 0パディングの強制
const paddingReplacer = (match, p1, p2, p3) => {
return [p1, p2.padStart(2, '0'), p3.padStart(2, '0')].join('/');
}

// Datepickerの非表示
const hide = () => $('#'+props.identifier).datepicker('hide');

// 入力値をクリアする
const clearValue = () => {
  emit('update:modelValue', '');
  updateInputElementValue('');
}

const onClearFooterClick = () => {
  if (!currentOpenIdentifier) {
    return;
  }
  clearHandlers.get(currentOpenIdentifier)?.();
  $('#' + currentOpenIdentifier).datepicker('hide');
}

const DATEPICKER_OBSERVER_OPTIONS = { attributes: true, attributeFilter: ['style'], childList: true };

// 都度呼び出して復元する
const ensureClearFooter = () => {
  const datepickerDiv = document.getElementById('ui-datepicker-div');
  if (!datepickerDiv || datepickerDiv.querySelector('.com_datepicker_clear1')) {
    return;
  }
  const footer = document.createElement('div');
  footer.className = 'com_datepicker_clear_footer1';

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'com_datepicker_clear1';
  button.textContent = '削除';
  button.addEventListener('click', onClearFooterClick);

  footer.appendChild(button);

  // 書き込み中だけ監視を止める
  positionObserver?.disconnect();
  datepickerDiv.appendChild(footer);
  if (positionObserver) {
    positionObserver.observe(datepickerDiv, DATEPICKER_OBSERVER_OPTIONS);
  }
}

// フォーカス時の標準スクロールを止める（PC対象外）
const onFocusPointerDown = (e) => {
  if (props.disabled || !Capacitor.isNativePlatform()) {
    return;
  }
  e.preventDefault();
  e.target.focus({ preventScroll: true });
}

const EDGE_MARGIN = 8;

const applyDatepickerPosition = () => {
  if (!isDatepickerOpen.value) {
    return;
  }
  if (currentOpenIdentifier !== props.identifier) {
    return;
  }
  const inputElement = document.getElementById(props.identifier);
  const datepickerDiv = document.getElementById('ui-datepicker-div');
  if (!inputElement || !datepickerDiv) {
    return;
  }
  const rect = inputElement.getBoundingClientRect();
  const calendarHeight = datepickerDiv.offsetHeight;
  const calendarWidth = datepickerDiv.offsetWidth;

  const spaceBelow = window.innerHeight - rect.bottom - EDGE_MARGIN;
  const top = spaceBelow >= calendarHeight || rect.top < calendarHeight
    ? rect.bottom
    : rect.top - calendarHeight;

  const left = Math.max(EDGE_MARGIN, Math.min(rect.left, window.innerWidth - calendarWidth - EDGE_MARGIN));

  // 書き込み中だけ監視を止める
  positionObserver?.disconnect();
  datepickerDiv.style.position = 'fixed';
  datepickerDiv.style.top = `${Math.max(EDGE_MARGIN, top)}px`;
  datepickerDiv.style.left = `${left}px`;
  if (positionObserver) {
    positionObserver.observe(datepickerDiv, DATEPICKER_OBSERVER_OPTIONS);
  }
}

// 要素の変化を監視して代用する
let positionObserver = null;

const onDatepickerMutated = () => {
  ensureClearFooter();
  applyDatepickerPosition();
}

const startWatchingDatepickerPosition = () => {
  const datepickerDiv = document.getElementById('ui-datepicker-div');
  if (!datepickerDiv) {
    return;
  }
  if (!positionObserver) {
    positionObserver = new MutationObserver(onDatepickerMutated);
  }
  positionObserver.observe(datepickerDiv, DATEPICKER_OBSERVER_OPTIONS);
}

const stopWatchingDatepickerPosition = () => {
  positionObserver?.disconnect();
}

onMounted(() => {
  clearHandlers.set(props.identifier, clearValue);

  $('#'+props.identifier).datepicker({
    dateFormat: 'yy/mm/dd',
    beforeShow: () => {
      isDatepickerOpen.value = true;
      currentOpenIdentifier = props.identifier;
      startWatchingDatepickerPosition();
    },
    onSelect: (dateText) => {
      // エンターキーによる発火を除外
      if (isEnterPressed.value) {
        return;
      }
      updateDateValue(dateText);
    },
    onClose: (dateText) => {
      isDatepickerOpen.value = false;
      if (currentOpenIdentifier === props.identifier) {
        currentOpenIdentifier = null;
      }
      stopWatchingDatepickerPosition();
      // エンターキーによる発火を除外
      if (isEnterPressed.value) {
        return;
      }
      updateDateValue(dateText);
    }
  });

  window.addEventListener('resize', applyDatepickerPosition);
  window.addEventListener('scroll', applyDatepickerPosition, { capture: true, passive: true });
})

onUnmounted(() => {
  clearHandlers.delete(props.identifier);
  stopWatchingDatepickerPosition();
  window.removeEventListener('resize', applyDatepickerPosition);
  window.removeEventListener('scroll', applyDatepickerPosition, { capture: true });
})

// 他コンポーネントから参照可能にするメソッドの宣言
defineExpose({hide});
</script>

<template>
  <input type="text" class="form-control com_datepicker_input1" v-bind:id="props.identifier"
         :value="modelValue" @paste.prevent :disabled="props.disabled" :readonly="props.readonly"
         @mousedown="onFocusPointerDown($event)" @touchstart="onFocusPointerDown($event)"
         @blur="onBlur($event)" @keydown.enter="onEnter($event)">
</template>
